/**
 * APP PROMOTION — /google-ads/app-promotion
 *
 * Lists the connected customer's Google App campaigns (MULTI_CHANNEL /
 * APP_CAMPAIGN) with store, app id, bidding goal, target CPA, budget and
 * Google's primary status; pause / resume inline; "New campaign" opens the
 * 3-step workspace.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight, Globe, Pause, Play, Plus, RefreshCw, Smartphone } from 'lucide-react';
import { AdGroupRows, ObjectiveWorkspaceConfig } from '@/components/google-ads/objectives/ObjectiveWorkspace';
import { GoogleAdsCampaignSummary } from '@/services/google-ads.service';
import { googleAdsService, GoogleAdsAppCampaign } from '@/services/google-ads.service';
import {
  alreadyToasted, errMsg, ErrorBox, formatMicros, Pill, Spinner, statusOf, useYoutubeAuth,
} from '@/components/google-ads/youtube/youtubeShared';
import { APP_BASE, goalMeta, storeLabel } from '@/components/google-ads/app-promotion/appPromotionShared';

/** Drill-down rows under each campaign. App campaigns hold exactly one ad group and one ad, so no "add" links. */
const APP_ROWS_CFG: ObjectiveWorkspaceConfig = {
  key: 'app', title: 'App Promotion', subtitle: '', base: APP_BASE, icon: Smartphone,
  channelTypes: ['MULTI_CHANNEL'], newCampaign: `${APP_BASE}/create`, childKind: 'adgroup',
};

export default function AppPromotionPage() {
  const navigate = useNavigate();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);

  const [rows, setRows] = useState<GoogleAdsAppCampaign[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!accountUid) return;
    setError(null);
    try {
      setRows(await googleAdsService.appPromotion.listCampaigns(accountUid));
    } catch (e) {
      setRows([]); setError(errMsg(e, 'Could not load app campaigns.'));
    }
  }, [accountUid]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  const toggle = async (c: GoogleAdsAppCampaign) => {
    const enable = c.status !== 'ENABLED';
    if (enable && !window.confirm(`Enable "${c.name}"? It will start spending its daily budget.`)) return;
    setBusy(c.id);
    try {
      if (enable) await googleAdsService.resumeCampaign(accountUid, c.id);
      else await googleAdsService.pauseCampaign(accountUid, c.id);
      toast.success(enable ? 'Campaign enabled' : 'Campaign paused');
      await load();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(errMsg(e));
    } finally { setBusy(null); }
  };

  return (
    <>
      <Topbar
        title="App Promotion"
        breadcrumb="Google Ads / App Promotion"
        description="Google App campaigns — installs and in-app actions across Play, Search, YouTube and Display"
        right={ready ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={load} iconLeft={<RefreshCw size={14} />}>Refresh</Button>
            <Button size="sm" onClick={() => navigate(`${APP_BASE}/create`)} iconLeft={<Plus size={14} />}>New campaign</Button>
          </div>
        ) : undefined}
      />

      <div className="w-full px-7 py-7">
        {auth === undefined && (
          <div className="flex min-h-[50vh] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        )}

        {auth !== undefined && !ready && (
          <EmptyState
            icon={<Globe size={36} className="text-text-muted" />}
            title="Connect a Google Ads account first"
            description="Authorise Google and attach a customer account, then come back to build App campaigns."
            action={<Link to="/google-ads/account"><Button iconLeft={<Globe size={14} />}>Connect Google Ads</Button></Link>}
          />
        )}

        {ready && (
          <div className="space-y-4">
            {error && <ErrorBox message={error} />}

            {rows === null && !error && (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="skeleton h-16 rounded-2xl" />)}</div>
            )}

            {rows !== null && rows.length === 0 && !error && (
              <EmptyState
                icon={<Smartphone size={36} className="text-text-muted" />}
                title="No App campaigns yet"
                description="Promote a Play or App Store app — Google places it across Play, Search, YouTube, Discover and Display automatically."
                action={<Button onClick={() => navigate(`${APP_BASE}/create`)} iconLeft={<Plus size={15} />}>New App campaign</Button>}
              />
            )}

            {rows !== null && rows.length > 0 && (
              <Card padded={false} className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-surface-alt/60 text-2xs uppercase tracking-wide text-text-muted">
                      <tr>
                        <th className="px-4 py-2.5 font-medium">Campaign</th>
                        <th className="px-4 py-2.5 font-medium">App</th>
                        <th className="px-4 py-2.5 font-medium">Goal</th>
                        <th className="px-4 py-2.5 font-medium">Target CPA</th>
                        <th className="px-4 py-2.5 font-medium">Daily budget</th>
                        <th className="px-4 py-2.5 font-medium">Status</th>
                        <th className="px-4 py-2.5" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {rows.map((c) => {
                        const st = statusOf(c.status, c.primaryStatus);
                        const goal = goalMeta(c.biddingStrategyGoalType);
                        const open = openId === c.id;
                        return (
                          <Fragment key={c.id}>
                          <tr className="hover:bg-surface-alt/40">
                            <td className="px-4 py-3">
                              <button type="button" onClick={() => setOpenId(open ? null : c.id)} className="flex items-start gap-1.5 text-left" aria-expanded={open} aria-label={`${open ? 'Hide' : 'Show'} ad group and ad for ${c.name}`}>
                                {open ? <ChevronDown size={14} className="mt-0.5 shrink-0 text-text-muted" /> : <ChevronRight size={14} className="mt-0.5 shrink-0 text-text-muted" />}
                                <span className="font-medium text-text-primary">{c.name}</span>
                              </button>
                              <div className="text-2xs text-text-muted">
                                #{c.id}{c.startDate ? ` · from ${c.startDate}` : ''}{c.endDate ? ` to ${c.endDate}` : ''}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-text-primary">
                                <Smartphone size={14} className="shrink-0 text-text-muted" />
                                <span className="truncate font-mono text-xs">{c.appId ?? '—'}</span>
                              </div>
                              <div className="text-2xs text-text-muted">{storeLabel(c.appStore)}</div>
                            </td>
                            <td className="px-4 py-3 text-xs text-text-primary">{goal?.short ?? (c.biddingStrategyGoalType?.replace(/_/g, ' ').toLowerCase() || '—')}</td>
                            <td className="px-4 py-3 text-xs text-text-primary">{formatMicros(c.targetCpaMicros, auth?.currency) ?? '—'}</td>
                            <td className="px-4 py-3 text-xs text-text-primary">{formatMicros(c.budgetAmountMicros, auth?.currency) ?? '—'}</td>
                            <td className="px-4 py-3">
                              <Pill label={st.label} tone={st.tone} />
                              {c.primaryStatusReasons && c.primaryStatusReasons.length > 0 && (
                                <div className="mt-1 max-w-[220px] text-[10px] leading-snug text-text-muted">
                                  {c.primaryStatusReasons.map((r) => r.replace(/_/g, ' ').toLowerCase()).join(', ')}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button variant="ghost" size="sm" disabled={busy === c.id} onClick={() => toggle(c)}
                                iconLeft={busy === c.id ? <Spinner size={13} /> : c.status === 'ENABLED' ? <Pause size={13} /> : <Play size={13} />}>
                                {c.status === 'ENABLED' ? 'Pause' : 'Enable'}
                              </Button>
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={7} className="p-0">
                                <AdGroupRows
                                  accountUid={accountUid}
                                  customerId={auth?.customer_id ?? ''}
                                  campaign={{ id: c.id, name: c.name, status: c.status, biddingStrategyType: c.biddingStrategyType, advertisingChannelType: 'MULTI_CHANNEL' } as GoogleAdsCampaignSummary}
                                  cfg={APP_ROWS_CFG}
                                />
                              </td>
                            </tr>
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </>
  );
}
